import { isValid, parseISO } from "date-fns";
import { addTimeZoneOffset, formatDateForTransaction } from "@helpers/date";
import {
    ACCRUAL_LINE_COMPARABLE_FIELDS,
    AccrualLineField,
    AccrualLineFieldValue,
    AccrualLineSnapshot,
    toAccrualLineArray,
} from "@common/helpers/accrualLineDeltas";
import {
    formatInvoiceAmount,
    formatInvoiceMoney,
    formatInvoiceQuantity,
} from "@common/helpers/nativeInvoice";
import { HISTORY_FIELD_LABELS } from "@common/constants/history-source-types";
import { AuditLog, AuditLogDiff } from "@common/types/auditLog";
import { getAccrualLineFieldLabel } from "./lineFieldLabels";

export interface AccrualLabelResolver {
    account: (id: string | null | undefined) => string | null;
    classLabel: (id: string | null | undefined) => string | null;
}

type DisplayValue = string | number | boolean | object | null | undefined;

export interface DisplayedValue {
    text: string;
    isEmpty: boolean;
}

interface AccrualLineFieldSummary {
    field: AccrualLineField;
    label: string;
    text: string;
}

// Matches `formatAuditLogRefValue` so the activity timeline and the history
// details drawer show the same placeholder for the same audit log.
const EMPTY_VALUE_TEXT = "-";

const MAX_DISPLAYED_TEXT_LENGTH = 120;

const MAX_DISPLAYED_JSON_LENGTH = 200;

const EMPTY_DISPLAYED_VALUE: DisplayedValue = {
    text: EMPTY_VALUE_TEXT,
    isEmpty: true,
};

const ACCOUNT_REFERENCE_FIELDS: ReadonlySet<AccrualLineField> = new Set([
    "accountId",
    "accountsPayableId",
    "accountsReceivableId",
]);

const CLASS_REFERENCE_FIELD: AccrualLineField = "classIds";

const CURRENCY_FIELD = "currency";

const LINES_FIELD = "lines";

const DATE_FIELDS: ReadonlySet<string> = new Set([
    "due",
    "paidAt",
    "uncollectableAt",
    "voidedAt",
]);

const MONETARY_FIELDS: ReadonlySet<string> = new Set([
    "total",
    "subtotal",
    "tax",
    "discount",
    "amount",
    "unitAmount",
]);

const QUANTITY_FIELD = "quantityDecimal";

const HISTORY_FIELD_LABEL_LOOKUP: Record<string, string> = HISTORY_FIELD_LABELS;

// Only fields whose accrual wording intentionally differs from the shared
// history labels; everything else falls through so the two cannot diverge.
const ACCRUAL_FIELD_LABEL_OVERRIDES: Record<string, string> = {
    financialAccountId: "financial account",
    invoiceNumber: "invoice number",
    isSubscription: "subscription flag",
    lines: "line items",
};

export function getAccrualFieldLabel(field: string): string {
    return (
        ACCRUAL_FIELD_LABEL_OVERRIDES[field] ??
        HISTORY_FIELD_LABEL_LOOKUP[field] ??
        field
    );
}

function isStringValue(value: DisplayValue): value is string {
    return typeof value === "string";
}

function isStringArray(value: DisplayValue): value is string[] {
    return Array.isArray(value) && value.every(isStringValue);
}

function isNonEmptyString(value: DisplayValue): value is string {
    return isStringValue(value) && value !== "";
}

/** Bill/invoice `date` columns reach the snapshot as bare `YYYY-MM-DD`. */
function isDateOnlyValue(value: string): boolean {
    return /^\d{4}-\d{2}-\d{2}$/.test(value);
}

function truncateText(text: string): string {
    return text.length > MAX_DISPLAYED_TEXT_LENGTH
        ? `${text.slice(0, MAX_DISPLAYED_TEXT_LENGTH)}…`
        : text;
}

/**
 * Renders a date field. Unparseable values pass through unchanged rather than
 * becoming the "-" that `formatDateForTransaction` returns for invalid dates.
 * Date-only values are already calendar days, so they skip the offset decoding
 * that timestamps need.
 */
function formatDateValue(value: string): string {
    const parsed = parseISO(value);
    if (!isValid(parsed)) {
        return truncateText(value);
    }
    return formatDateForTransaction(
        isDateOnlyValue(value) ? parsed : addTimeZoneOffset(parsed),
    );
}

function formatObjectFallback(value: object): string {
    try {
        const json = JSON.stringify(value);
        if (json === undefined) {
            return "(unsupported value)";
        }
        return json.length > MAX_DISPLAYED_JSON_LENGTH
            ? `${json.slice(0, MAX_DISPLAYED_JSON_LENGTH)}…`
            : json;
    } catch {
        return "(unsupported value)";
    }
}

function isEmptyDisplayValue(value: DisplayValue): boolean {
    if (value === null || value === undefined || value === "") {
        return true;
    }
    return Array.isArray(value) && value.length === 0;
}

function formatPrimitive(value: DisplayValue): string {
    if (isEmptyDisplayValue(value)) {
        return EMPTY_VALUE_TEXT;
    }
    if (Array.isArray(value)) {
        return truncateText(
            value.map((item) => formatPrimitive(item)).join(", "),
        );
    }
    if (typeof value === "object" && value !== null) {
        return formatObjectFallback(value);
    }
    if (typeof value === "boolean") {
        return value ? "yes" : "no";
    }
    return truncateText(String(value));
}

/**
 * Field-aware rendering: dates, money and quantities are recognised by field
 * name rather than by sniffing the value, so free text can never be reformatted
 * as a date and a monetary column never renders as a bare number.
 */
function formatFieldValue({
    field,
    value,
    currency,
}: {
    field: string;
    value: DisplayValue;
    currency: string | null;
}): string {
    if (isEmptyDisplayValue(value)) {
        return EMPTY_VALUE_TEXT;
    }
    if (DATE_FIELDS.has(field) && isStringValue(value)) {
        return formatDateValue(value);
    }
    if (typeof value === "number" && Number.isFinite(value)) {
        if (MONETARY_FIELDS.has(field)) {
            return currency
                ? formatInvoiceMoney(value, currency)
                : formatInvoiceAmount(value);
        }
        if (field === QUANTITY_FIELD) {
            return formatInvoiceQuantity(value);
        }
    }
    return formatPrimitive(value);
}

/**
 * Currency of the audited document, needed to render its monetary fields. Only
 * changed fields reach the diff, so it is taken from the currency change when
 * there is one and from the audited lines otherwise.
 */
export function getAccrualCurrency(log: AuditLog): string | null {
    const currencyDiff = log.diff.find((diff) => diff.field === CURRENCY_FIELD);
    const fromCurrencyDiff =
        currencyDiff?.after?.value ?? currencyDiff?.before?.value;
    if (isNonEmptyString(fromCurrencyDiff)) {
        return fromCurrencyDiff;
    }

    const linesDiff = log.diff.find((diff) => diff.field === LINES_FIELD);
    const lines = [
        ...toAccrualLineArray(linesDiff?.after?.value),
        ...toAccrualLineArray(linesDiff?.before?.value),
    ];
    return (
        lines.find((line) => isNonEmptyString(line.currency))?.currency ?? null
    );
}

export function describeDiffSide({
    refValue,
    field,
    currency,
}: {
    refValue: AuditLogDiff["before"];
    field: string;
    currency: string | null;
}): DisplayedValue {
    if (refValue.valueLabel != null && refValue.valueLabel !== "") {
        return { text: truncateText(refValue.valueLabel), isEmpty: false };
    }
    return {
        text: formatFieldValue({ field, value: refValue.value, currency }),
        isEmpty: isEmptyDisplayValue(refValue.value),
    };
}

export function describeLineFieldValue({
    field,
    value,
    currency,
    resolver,
}: {
    field: AccrualLineField;
    value: AccrualLineFieldValue;
    currency: string | null;
    resolver: AccrualLabelResolver;
}): DisplayedValue {
    if (ACCOUNT_REFERENCE_FIELDS.has(field)) {
        if (!isNonEmptyString(value)) {
            return EMPTY_DISPLAYED_VALUE;
        }
        return {
            text: truncateText(resolver.account(value) ?? value),
            isEmpty: false,
        };
    }
    if (field === CLASS_REFERENCE_FIELD) {
        if (!isStringArray(value) || value.length === 0) {
            return EMPTY_DISPLAYED_VALUE;
        }
        return {
            text: truncateText(
                value.map((id) => resolver.classLabel(id) ?? id).join(", "),
            ),
            isEmpty: false,
        };
    }
    return {
        text: formatFieldValue({ field, value, currency }),
        isEmpty: isEmptyDisplayValue(value),
    };
}

/**
 * Fields worth showing for a line that was added or removed as a whole. The
 * description is omitted because it is already carried by the line label. Zero
 * amounts and quantities are kept — for an added or removed line this summary is
 * the only place they appear.
 */
export function getLineFieldSummaries(
    line: AccrualLineSnapshot,
    resolver: AccrualLabelResolver,
): AccrualLineFieldSummary[] {
    const currency = line.currency ?? null;
    return ACCRUAL_LINE_COMPARABLE_FIELDS.filter(
        (field) => field !== "description" && !isEmptyDisplayValue(line[field]),
    ).map((field) => ({
        field,
        label: getAccrualLineFieldLabel(field),
        text: describeLineFieldValue({
            field,
            value: line[field],
            currency,
            resolver,
        }).text,
    }));
}

/**
 * Phrase-ready label for a line, so it reads naturally after the "line item"
 * noun the surrounding sentences supply ("added line item #3").
 */
export function getLineLabel(
    line: AccrualLineSnapshot,
    position: number,
): string {
    const description = line.description?.trim();
    return description
        ? `"${truncateText(description)}"`
        : `#${Math.max(position, 0) + 1}`;
}
